import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { BusinessUnit } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/businessUnit/BusinessUnit'
import { FocusArea } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/focusArea/FocusArea'
import { ValueTypeAndCurrency } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/commonComponents/editorComponents/components/valueTypeAndCurrency/ValueTypeAndCurrency'
import { useFocusAreaValues } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/hooks/useFocusAreaValues'
import { useIncremental } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/hooks/useIncremental'
import { KeyInitiativeInitTypes } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/models/keyInitiativeInitTypes'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { IJBPActualsAndTargetsCategory } from 'legacy/lut/services/partner360/common/models/jbpActualsAndTargetsCategories'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { updateFormDataField, useGetFormData } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Grid, Item } from 'core-components/library/grid'
import { LoaderOverlay } from 'core-components/library/loader'

import { useBusinessUnitsData } from '../../../../../../hooks/useBusinessUnits'
import { TRANSLATION_KEYS as tr } from '../../../../../../translations'
import {
  EXTENDED_BUSINESS_UNITS_BLANK,
  EXTENDED_BUSINESS_UNITS_PRINT,
  EXTENDED_BUSINESS_UNITS_PS,
  EXTENDED_FOCUS_AREA,
  IS_INCREMENTAL_MODE_LOCKED_BY_FUNDING_SOURCE,
} from '../../../configs'
import { finSobValidation } from '../../../helpers/finSobValidation'
import {
  EKeyInitiativeGoalMode,
  isIncrementalForcedByInvestmentSource,
  shouldAddToBridgesForGoalMode,
} from '../../../helpers/keyInitiativeGoalMode'
import { IKeyInitiativeSectionProps } from '../../../models/IKeyInitiativeSectionProps'
import {
  IKeyInitiativeForm,
  KEY_INITIATIVE_SALES_TARGET_FIELDS,
  TKeyInitiativeEstimateSalesTargetField,
} from '../../../models/keyInitiativeForm'

import { KeyInitiativeOutcomeSection } from './components/keyInitiativeOutcomeSection'

import styles from './GoalSection.module.scss'

interface IGoalSectionProps extends IKeyInitiativeSectionProps {
  hasEstimateSalesValidationError: boolean
}

export const GoalSection = (props: IGoalSectionProps) => {
  const {
    initialData,
    programType,
    finSobData,
    otherInitiatives,
    fYear,
    keyObjectives,
    initiativeFormKey,
    hasEstimateSalesValidationError,
  } = props

  /** -------------------- State ----------------------- */

  const [isGoalWarning, setIsGoalWarning] = useState(false)
  const [isGoalError, setIsGoalError] = useState(false)
  const [isGoalBelowMinimum, setIsGoalBelowMinimum] = useState(false)
  const [planValue, setPlanValue] = useState(0)
  const [originalPlanValue, setOriginalPlanValue] = useState(0)

  /** -------------------- Selectors ----------------------- */

  const translationMode = useSelector(selectTranslationMode)

  /** -------------------- Hooks ----------------------- */

  const dispatch = useAppDispatch()
  const [{ formData }] = useGetFormData(initiativeFormKey, initialData)
  const { incrementalFieldNameByValueType } = useIncremental<IKeyInitiativeForm>({
    initialData,
    formData,
  })
  const {
    businessUnits,
    isLoading: isBusinessUnitsLoading,
    isLoaded: isBusinessUnitsLoaded,
  } = useBusinessUnitsData({ programType, fYear })

  /** -------------------- Variables ----------------------- */

  const businessUnitsExtended = useMemo(() => {
    // A partial list makes Dropdown clear valid SI defaults before the dictionary arrives.
    if (!isBusinessUnitsLoaded) return []

    let extendedBusinessUnits: IJBPActualsAndTargetsCategory[] = businessUnits
    if (programType === ProgramTypes.PC || programType === ProgramTypes.PersonalSystems) {
      extendedBusinessUnits = [
        ...EXTENDED_BUSINESS_UNITS_PS.map((el) => ({ ...el, ProgramType: programType })),
        ...businessUnits,
        ...EXTENDED_BUSINESS_UNITS_BLANK.map((el) => ({ ...el, ProgramType: programType })),
      ]
    } else if (programType === ProgramTypes.CorePrint || programType === ProgramTypes.Print) {
      extendedBusinessUnits = [
        ...EXTENDED_BUSINESS_UNITS_PRINT.map((el) => ({ ...el, ProgramType: programType })),
        ...businessUnits,
        ...EXTENDED_BUSINESS_UNITS_BLANK.map((el) => ({ ...el, ProgramType: programType })),
      ]
    }

    return extendedBusinessUnits
  }, [businessUnits, isBusinessUnitsLoaded, programType])

  const keyObjectivesSettingsList = useMemo(() => {
    return keyObjectives
  }, [keyObjectives])

  const isAutoCreatedKeyInitiative = useMemo(
    () => formData?.initType === KeyInitiativeInitTypes.ImportedInitiative,
    [formData?.initType]
  )

  const focusAreaValues = useFocusAreaValues({
    keyObjectives: keyObjectivesSettingsList,
    keyObjective: formData?.keyObjective,
  })

  const goalFieldName = useMemo(() => {
    return `goal${formData?.valueType}`
  }, [formData.valueType])

  const isGoalModeReadOnly =
    IS_INCREMENTAL_MODE_LOCKED_BY_FUNDING_SOURCE &&
    isIncrementalForcedByInvestmentSource(formData?.hpInvestmentSource)
  const goalMode = isGoalModeReadOnly
    ? EKeyInitiativeGoalMode.Incremental
    : (formData?.goalMode ?? initialData?.goalMode ?? EKeyInitiativeGoalMode.RunRate)
  const isIncrementalMode = goalMode === EKeyInitiativeGoalMode.Incremental
  const isNoSalesTarget = goalMode === EKeyInitiativeGoalMode.NoSalesTarget
  const runRateEstimateSalesTargetField = goalFieldName as TKeyInitiativeEstimateSalesTargetField
  const incrementalEstimateSalesTargetField =
    incrementalFieldNameByValueType as TKeyInitiativeEstimateSalesTargetField
  const estimateSalesTargetField = isIncrementalMode
    ? incrementalEstimateSalesTargetField
    : runRateEstimateSalesTargetField

  const isTranslationMode = useMemo(() => translationMode === 'edit', [translationMode])

  /** -------------------- Methods ----------------------- */

  const handleGoalModeChange = useCallback(
    (mode: EKeyInitiativeGoalMode) => {
      if (mode === EKeyInitiativeGoalMode.NoSalesTarget) {
        for (const field of KEY_INITIATIVE_SALES_TARGET_FIELDS) {
          dispatch(updateFormDataField({ formKey: initiativeFormKey, field, value: 0 }))
        }
      } else if (mode !== goalMode) {
        const nextEstimateSalesTargetField =
          mode === EKeyInitiativeGoalMode.Incremental
            ? incrementalEstimateSalesTargetField
            : runRateEstimateSalesTargetField
        const estimateSalesValue =
          formData?.[estimateSalesTargetField] ?? initialData?.[estimateSalesTargetField] ?? null

        if (nextEstimateSalesTargetField !== estimateSalesTargetField) {
          dispatch(
            updateFormDataField({
              formKey: initiativeFormKey,
              field: nextEstimateSalesTargetField,
              value: estimateSalesValue,
            })
          )
          dispatch(
            updateFormDataField({
              formKey: initiativeFormKey,
              field: estimateSalesTargetField,
              value: null,
            })
          )
        }
      }

      dispatch(updateFormDataField({ formKey: initiativeFormKey, field: 'goalMode', value: mode }))
      dispatch(
        updateFormDataField({
          formKey: initiativeFormKey,
          field: 'shouldAddToBridges',
          value: shouldAddToBridgesForGoalMode(mode),
        })
      )
    },
    [
      dispatch,
      estimateSalesTargetField,
      formData,
      goalMode,
      incrementalEstimateSalesTargetField,
      initialData,
      initiativeFormKey,
      runRateEstimateSalesTargetField,
    ]
  )

  /** -------------------- Effects ----------------------- */

  useEffect(() => {
    if (!isNoSalesTarget || isAutoCreatedKeyInitiative) return

    for (const field of KEY_INITIATIVE_SALES_TARGET_FIELDS) {
      if (formData?.[field] !== 0) {
        dispatch(updateFormDataField({ formKey: initiativeFormKey, field, value: 0 }))
      }
    }
  }, [
    dispatch,
    formData,
    incrementalEstimateSalesTargetField,
    initiativeFormKey,
    isAutoCreatedKeyInitiative,
    isNoSalesTarget,
    runRateEstimateSalesTargetField,
  ])

  useEffect(() => {
    if (finSobData) {
      const { isWarning, isError, isGoalBelowMinimum, planValue, originalPlanValue } =
        finSobValidation(formData, finSobData, estimateSalesTargetField, otherInitiatives)
      setIsGoalWarning(isWarning)
      setIsGoalError(isError)
      setIsGoalBelowMinimum(isGoalBelowMinimum)
      setPlanValue(planValue)
      setOriginalPlanValue(originalPlanValue)
    }
  }, [estimateSalesTargetField, finSobData, formData, otherInitiatives])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <LoaderOverlay show={isBusinessUnitsLoading} />

      <Grid noRootMargin direction="row">
        <Item xs={6} className={styles.labelItem}>
          <Translation text={tr.keyInitiativeEditor.businessUnitAndFocusFieldGroup.unitLabel} />
        </Item>
        <Item xs={6} className={styles.labelItem}>
          <Translation text={tr.keyInitiativeEditor.businessUnitAndFocusFieldGroup.focusLabel} />
        </Item>

        <Item xs={6}>
          <BusinessUnit
            key={`${programType}:${fYear}`}
            isDisabled={initialData?.isFromGlobalPartner || !isBusinessUnitsLoaded}
            formKey={initiativeFormKey}
            initialValue={initialData?.businessUnit}
            businessUnits={businessUnitsExtended}
            clearFocusAreaOnChange={false}
            className={styles.formField}
            placeholder={tr.keyInitiativeEditor.businessUnitAndFocusFieldGroup.unitPlaceholder}
          />
          {isTranslationMode && (
            <Translation
              text={tr.keyInitiativeEditor.businessUnitAndFocusFieldGroup.unitPlaceholder}
            />
          )}
        </Item>
        <Item xs={6}>
          <FocusArea
            formKey={initiativeFormKey}
            isDisabled={isAutoCreatedKeyInitiative || initialData?.isFromGlobalPartner}
            initialValue={initialData?.focusArea}
            focusAreaValues={focusAreaValues}
            className={styles.formField}
            fYear={fYear}
            programType={programType}
            extendedItems={EXTENDED_FOCUS_AREA}
          />
        </Item>
      </Grid>

      {!isAutoCreatedKeyInitiative && (
        <>
          <Grid noRootMargin direction="row" className={styles.fieldsetContainer}>
            <Item xs={12} className={styles.sectionHeading}>
              <Translation text={tr.keyInitiativeEditor.goalFieldGroup.groupLabel} />
            </Item>

            <Item xs={12}>
              <div className={styles.goalSettingsControls}>
                <div className={styles.valueTypeControls}>
                  <ValueTypeAndCurrency
                    formKey={initiativeFormKey}
                    initialCurrency={initialData?.currency}
                    initialValueType={initialData?.valueType}
                    size={'small'}
                  />
                </div>
                <div className={styles.goalModeControls}>
                  <div className={styles.separator} />
                  <SegmentedControl
                    useItemKeys
                    noWrap
                    size={'small'}
                    selectedItem={goalMode}
                    onItemSelect={handleGoalModeChange}
                    isDisabled={initialData?.isFromGlobalPartner || isGoalModeReadOnly}
                  >
                    <SegmentedControl.Item
                      itemKey={EKeyInitiativeGoalMode.RunRate}
                      key={EKeyInitiativeGoalMode.RunRate}
                    >
                      <Translation text={tr.keyInitiativeEditor.goalFieldGroup.runRateMode} span />
                    </SegmentedControl.Item>
                    <SegmentedControl.Item
                      itemKey={EKeyInitiativeGoalMode.Incremental}
                      key={EKeyInitiativeGoalMode.Incremental}
                    >
                      <Translation
                        text={tr.keyInitiativeEditor.goalFieldGroup.incrementalMode}
                        span
                      />
                    </SegmentedControl.Item>
                    <SegmentedControl.Item
                      itemKey={EKeyInitiativeGoalMode.NoSalesTarget}
                      key={EKeyInitiativeGoalMode.NoSalesTarget}
                    >
                      <Translation
                        text={tr.keyInitiativeEditor.goalFieldGroup.noSalesTargetMode}
                        span
                      />
                    </SegmentedControl.Item>
                  </SegmentedControl>
                </div>
              </div>
            </Item>
          </Grid>
        </>
      )}

      <Grid noRootMargin direction="row" className={styles.outcomeContainer}>
        <Item xs={12}>
          <KeyInitiativeOutcomeSection
            formKey={initiativeFormKey}
            initialData={initialData}
            isDisabled={initialData?.isFromGlobalPartner}
            isFinancialReadOnly={isAutoCreatedKeyInitiative}
            estimateSalesTargetField={estimateSalesTargetField}
            isIncrementalMode={isIncrementalMode}
            isNoSalesTarget={isNoSalesTarget}
            hasEstimateSalesValidationError={hasEstimateSalesValidationError}
            planValue={planValue}
            originalPlanValue={originalPlanValue}
            isGoalWarning={isGoalWarning}
            isGoalError={isGoalError}
            isGoalBelowMinimum={isGoalBelowMinimum}
          />
        </Item>
      </Grid>
    </div>
  )
}
